import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Card, Typography, Button, Switch, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Fade, CircularProgress, Divider, Chip, Grid, FormControlLabel,
  ToggleButtonGroup, ToggleButton, Alert, Snackbar, InputBase,
  useTheme, alpha
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import config from "../../../config";

// --- Subcomponent: DualRangeInput ---
const DualRangeInput = ({ minVal, maxVal, onMinChange, onMaxChange, disabled }) => {
  const spinCSS = {
    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { display: 'none', WebkitAppearance: 'none', margin: 0 },
    '& input[type=number]': { MozAppearance: 'textfield' }
  };
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center',
      bgcolor: disabled ? 'transparent' : 'background.paper',
      border: '1px solid', borderColor: disabled ? 'transparent' : 'divider',
      borderRadius: 1.5, overflow: 'hidden', width: 'max-content', mx: 'auto'
    }}>
      <InputBase
        type="number" placeholder="Min" value={minVal} onChange={(e) => onMinChange(e.target.value)}
        disabled={disabled} sx={spinCSS}
        inputProps={{ style: { textAlign: 'center', fontSize: '0.875rem', padding: '6px 4px', width: '55px' } }}
      />
      <Box sx={{ width: '1px', height: 20, bgcolor: disabled ? 'transparent' : 'divider' }} />
      <InputBase
        type="number" placeholder="Max" value={maxVal} onChange={(e) => onMaxChange(e.target.value)}
        disabled={disabled} sx={spinCSS}
        inputProps={{ style: { textAlign: 'center', fontSize: '0.875rem', padding: '6px 4px', width: '55px' } }}
      />
    </Box>
  );
};

const POSEditFilters = () => {
  const { store_id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // generic state
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // filter states
  const [potencyFilterEnabled, setPotencyFilterEnabled] = useState(true);
  const [categoryConfig, setCategoryConfig] = useState({});
  const [moodConfig, setMoodConfig] = useState({});
  const [budgetRanges, setBudgetRanges] = useState([]);
  
  const [allCategories, setAllCategories] = useState([]);
  const [allMoods, setAllMoods] = useState([]);

  useEffect(() => {
    fetchFilters();
    // eslint-disable-next-line
  }, [store_id]);

  const fetchFilters = async () => {
    setInitialLoading(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/client/pos/get-chatbot-filters/${store_id}`, {
        headers: config.getHeaders()
      });
      const result = await res.json();
      if (!result.success) throw new Error(result.message);

      const data = result.data;
      setPotencyFilterEnabled(data.potency_enabled);
      
      const cats = data.categories || {};
      setCategoryConfig(cats);
      setAllCategories(Object.keys(cats));

      const mds = data.moods || {};
      setMoodConfig(mds);
      setAllMoods(Object.keys(mds));

      setBudgetRanges(data.budgets || []);

    } catch (err) {
      setError(err.message || "Failed to load chatbot filters.");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleCategoryConfigChange = (category, field, subfield, value) => {
    if (field === 'is_visible' && value === true) {
      const activeCount = Object.values(categoryConfig).filter(c => c.is_visible).length;
      if (activeCount >= 9) {
        setSnackbar({ open: true, message: "You can only have up to 9 visible categories.", severity: "warning" });
        return;
      }
    }
    setCategoryConfig(prev => {
      const newConfig = { ...prev };
      if (subfield) {
        newConfig[category][field][subfield] = value;
      } else {
        newConfig[category][field] = value;
      }
      return newConfig;
    });
  };

  const handleMoodConfigChange = (mood, field, value) => {
    if (field === 'is_visible' && value === true) {
      const activeCount = Object.values(moodConfig).filter(m => m.is_visible).length;
      if (activeCount >= 12) {
        setSnackbar({ open: true, message: "You can only have up to 12 visible moods.", severity: "warning" });
        return;
      }
    }
    setMoodConfig(prev => ({
      ...prev,
      [mood]: { ...prev[mood], [field]: value }
    }));
  };

  const handleBudgetChange = (idx, field, value) => {
    setBudgetRanges(prev => {
      const newB = [...prev];
      newB[idx][field] = value;
      return newB;
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        client_store_id: store_id,
        filter_config: {
          potency_enabled: potencyFilterEnabled,
          mood_enabled: true,
          budget_enabled: true,
          categories: categoryConfig,
          moods: moodConfig,
          budgets: budgetRanges
        }
      };

      const res = await fetch(`${config.API_BASE_URL}/client/pos/update-chatbot-filters`, {
        method: "POST",
        headers: config.getHeaders(),
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.message);

      setSnackbar({ open: true, message: "✅ Chatbot filters updated successfully!", severity: "success" });
      setTimeout(() => navigate("/pos-sync"), 2000);

    } catch (err) {
      setError(err.message || "Failed to save filters.");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', height: '50vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", py: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/pos-sync")} sx={{ mr: 2 }}>Back</Button>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Edit Chatbot Filters</Typography>
      </Box>

      <Card sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)" }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Configure visibility, icons, and ranges for the Chatbot filters. Need icons? <a href="https://heroicons.com/" target="_blank" rel="noreferrer" style={{ color: theme.palette.primary.main, fontWeight: 600 }}>Find SVG icons Here</a>.
        </Typography>

        {/* SECTION: CATEGORIES */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Product Categories</Typography>
            <Typography variant="body2" color="text.secondary">Select up to 9 categories to display.</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Icon SVG Code</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Preview</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Visibility (Max 9)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allCategories.map((cat) => {
                  const conf = categoryConfig[cat];
                  const isVisible = conf?.is_visible || false;
                  return (
                    <TableRow key={cat} hover sx={{ opacity: isVisible ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                      <TableCell sx={{ fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{cat}</TableCell>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                        <TextField
                          fullWidth size="small" spellCheck="false"
                          value={conf?.icon_svg || ""}
                          onChange={(e) => handleCategoryConfigChange(cat, 'icon_svg', null, e.target.value)}
                          placeholder="<svg>...</svg>"
                          disabled={!isVisible}
                          inputProps={{ maxLength: 2000 }}
                          sx={{ '& input': { fontSize: '0.85rem', fontFamily: 'monospace', color: 'text.secondary' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ width: 32, height: 32, mx: 'auto', opacity: isVisible ? 1 : 0.4, '& svg': { width: '100%', height: '100%', color: 'text.secondary' } }} dangerouslySetInnerHTML={{ __html: conf?.icon_svg || "" }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Switch checked={isVisible} onChange={(e) => handleCategoryConfigChange(cat, 'is_visible', null, e.target.checked)} color="primary" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider sx={{ my: 6, borderColor: alpha(theme.palette.divider, 0.8), borderStyle: 'dashed' }} />

        {/* SECTION: MOODS */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Mood Configuration</Typography>
            <Typography variant="body2" color="text.secondary">Select up to 12 moods to display, and assign them to your active categories.</Typography>
          </Box>
          <TableContainer sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Mood</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Icon SVG Code</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Preview</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Visibility (Max 12)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allMoods.map((mood) => {
                  const conf = moodConfig[mood];
                  const isVisible = conf?.is_visible || false;
                  return (
                    <TableRow key={mood} hover sx={{ opacity: isVisible ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                      <TableCell sx={{ fontWeight: 600, textTransform: 'capitalize', borderBottom: '1px solid', borderColor: 'divider' }}>{mood}</TableCell>
                      <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                        <TextField
                          fullWidth size="small" spellCheck="false"
                          value={conf?.icon_svg || ""}
                          onChange={(e) => handleMoodConfigChange(mood, 'icon_svg', e.target.value)}
                          placeholder="<svg>...</svg>" disabled={!isVisible} inputProps={{ maxLength: 2000 }}
                          sx={{ '& input': { fontSize: '0.85rem', fontFamily: 'monospace', color: 'text.secondary' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ width: 32, height: 32, mx: 'auto', opacity: isVisible ? 1 : 0.4, '& svg': { width: '100%', height: '100%', color: 'text.secondary' } }} dangerouslySetInnerHTML={{ __html: conf?.icon_svg || "" }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Switch checked={isVisible} onChange={(e) => handleMoodConfigChange(mood, 'is_visible', e.target.checked)} color="primary" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="primary.main" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, mb: 3 }}>
              Mood Assignment
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>Currently Active Moods:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {allMoods.filter(m => moodConfig[m]?.is_visible).length > 0 ? (
                  allMoods.filter(m => moodConfig[m]?.is_visible).map(m => (
                    <Chip key={m} label={m} size="small" sx={{ fontWeight: 600, textTransform: 'capitalize', bgcolor: '#ffffff', border: '1px solid', borderColor: 'divider' }} />
                  ))
                ) : (
                  <Typography variant="body2" color="text.disabled">None selected</Typography>
                )}
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}>Enable mood selection for these categories:</Typography>
            <Grid container spacing={2}>
              {allCategories.filter(c => categoryConfig[c]?.is_visible).map(cat => (
                <Grid item xs={12} sm={6} md={4} key={cat}>
                  <Box
                    sx={{
                      px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      bgcolor: '#ffffff', borderRadius: 2, border: '1px solid',
                      borderColor: categoryConfig[cat]?.ask_mood ? 'primary.main' : 'divider',
                      transition: 'all 0.2s ease', cursor: 'pointer',
                      boxShadow: categoryConfig[cat]?.ask_mood ? `0 0 0 1px ${theme.palette.primary.main}` : 'none'
                    }}
                    onClick={() => handleCategoryConfigChange(cat, 'ask_mood', null, !categoryConfig[cat]?.ask_mood)}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: categoryConfig[cat]?.ask_mood ? 'primary.main' : 'text.primary' }}>{cat}</Typography>
                    <Switch
                      checked={categoryConfig[cat]?.ask_mood || false}
                      onChange={(e) => handleCategoryConfigChange(cat, 'ask_mood', null, e.target.checked)}
                      onClick={(e) => e.stopPropagation()} color="primary" size="small"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Divider sx={{ my: 6, borderColor: alpha(theme.palette.divider, 0.8), borderStyle: 'dashed' }} />

        {/* SECTION: BUDGETS */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Budget Tiers</Typography>
            <Typography variant="body2" color="text.secondary">Define the global budget options shown to the user.</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Display Text</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>From ($)</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>To ($)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {budgetRanges.map((br, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}><TextField size="small" fullWidth value={br.display_text} onChange={(e) => handleBudgetChange(idx, 'display_text', e.target.value)} inputProps={{ maxLength: 50 }} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}><TextField type="number" size="small" fullWidth value={br.min} onChange={(e) => handleBudgetChange(idx, 'min', e.target.value)} inputProps={{ max: 10000 }} /></TableCell>
                    <TableCell sx={{ borderBottom: '1px solid', borderColor: 'divider' }}><TextField type="number" size="small" fullWidth value={br.max} onChange={(e) => handleBudgetChange(idx, 'max', e.target.value)} inputProps={{ max: 10000 }} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Divider sx={{ my: 6, borderColor: alpha(theme.palette.divider, 0.8), borderStyle: 'dashed' }} />

        {/* SECTION: POTENCY */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>Potency Configuration</Typography>
              <Typography variant="body2" color="text.secondary">Configure min/max thresholds for low, medium, and high potency.</Typography>
            </Box>
            <FormControlLabel
              control={<Switch checked={potencyFilterEnabled} onChange={(e) => setPotencyFilterEnabled(e.target.checked)} color="primary" />}
              label={<Typography sx={{ fontWeight: 600, color: potencyFilterEnabled ? 'primary.main' : 'text.secondary' }}>{potencyFilterEnabled ? "Enabled" : "Disabled"}</Typography>}
              labelPlacement="start" sx={{ mr: 0, bgcolor: alpha(theme.palette.primary.main, 0.04), pr: 2, pl: 1, py: 0.5, borderRadius: 2 }}
            />
          </Box>

          {potencyFilterEnabled && (
            <Fade in={potencyFilterEnabled}>
              <Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Enable?</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', py: 2 }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Low (Min - Max)</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>Medium (Min - Max)</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', border: 'none', textAlign: 'center', py: 2 }}>High (Min - Max)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allCategories.filter(c => categoryConfig[c]?.is_visible).length === 0 ? (
                        <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, border: 'none' }}><Typography variant="body2" color="text.disabled">No visible categories selected.</Typography></TableCell></TableRow>
                      ) : (
                        allCategories.filter(c => categoryConfig[c]?.is_visible).map((cat) => {
                          const conf = categoryConfig[cat];
                          const isEnabled = conf?.ask_potency;
                          return (
                            <TableRow key={cat} hover sx={{ opacity: isEnabled ? 1 : 0.5, transition: 'opacity 0.2s' }}>
                              <TableCell sx={{ fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>{cat}</TableCell>
                              <TableCell sx={{ textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Switch checked={isEnabled} onChange={(e) => handleCategoryConfigChange(cat, 'ask_potency', null, e.target.checked)} color="success" size="small" />
                              </TableCell>
                              <TableCell sx={{ opacity: isEnabled ? 1 : 0.4, pointerEvents: isEnabled ? 'auto' : 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                <ToggleButtonGroup
                                  value={conf?.potency_unit || 'pct'} exclusive size="small" sx={{ height: 32 }}
                                  onChange={(e, newType) => { if (newType) handleCategoryConfigChange(cat, 'potency_unit', null, newType); }}
                                >
                                  <ToggleButton value="pct" sx={{ px: 1.5, py: 0, textTransform: 'none', fontWeight: 600 }}>%</ToggleButton>
                                  <ToggleButton value="mg" sx={{ px: 1.5, py: 0, textTransform: 'none', fontWeight: 600 }}>mg</ToggleButton>
                                </ToggleButtonGroup>
                              </TableCell>
                              <TableCell sx={{ opacity: isEnabled ? 1 : 0.4, pointerEvents: isEnabled ? 'auto' : 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                <DualRangeInput minVal={conf?.low?.min || ""} maxVal={conf?.low?.max || ""} onMinChange={(val) => handleCategoryConfigChange(cat, 'low', 'min', val)} onMaxChange={(val) => handleCategoryConfigChange(cat, 'low', 'max', val)} disabled={!isEnabled} />
                              </TableCell>
                              <TableCell sx={{ opacity: isEnabled ? 1 : 0.4, pointerEvents: isEnabled ? 'auto' : 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                <DualRangeInput minVal={conf?.medium?.min || ""} maxVal={conf?.medium?.max || ""} onMinChange={(val) => handleCategoryConfigChange(cat, 'medium', 'min', val)} onMaxChange={(val) => handleCategoryConfigChange(cat, 'medium', 'max', val)} disabled={!isEnabled} />
                              </TableCell>
                              <TableCell sx={{ opacity: isEnabled ? 1 : 0.4, pointerEvents: isEnabled ? 'auto' : 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                <DualRangeInput minVal={conf?.high?.min || ""} maxVal={conf?.high?.max || ""} onMinChange={(val) => handleCategoryConfigChange(cat, 'high', 'min', val)} onMaxChange={(val) => handleCategoryConfigChange(cat, 'high', 'max', val)} disabled={!isEnabled} />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Fade>
          )}
        </Box>

        <Box sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="contained" size="large" onClick={handleSave} disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AssignmentTurnedInIcon />}
            sx={{ py: 1.5, px: 6, borderRadius: 2, fontWeight: 700, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
          >
            {loading ? "Saving Changes..." : "Save Configuration"}
          </Button>
        </Box>

      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POSEditFilters;
